import type { ApiClient } from '@carbonwebmail/api-client'
import type { ContactFieldDto, ContactSummaryDto } from '@carbonwebmail/contracts'
import { useState } from 'react'
import { Alert } from '../../ui/Alert.js'
import { Dialog } from '../../ui/Dialog.js'
import { ContactFields, STATES } from './ContactFields.js'

/**
 * Changing what is known about one contact.
 *
 * Opened from the row it is about, prefilled with what that row holds — so the
 * form is the contact rather than a blank one asking for it again. The board
 * reloads on save, because the thing somebody came to do is see the row change.
 *
 * **The address is shown and cannot be changed.** Suppression is keyed on
 * `(org_id, email)` and so is the consent log, so renaming somebody's address
 * would move them out from under their own unsubscribe and their own history in
 * one write, with nothing to say it had happened. Somebody whose address
 * changed is a different contact. It is on screen rather than absent because a
 * form that simply omitted it would look like it had lost it.
 *
 * **Changing the subscription state writes a consent event.** It does not claim
 * consent and the note in the log says so: what it records is that a person did
 * this, and what the state was before. Without it the record would show an
 * unsubscribe followed by mail going out and nothing in between. (I6)
 */
export function EditContactDialog({
  client,
  contact,
  fields,
  onSaved,
  onClose,
}: {
  readonly client: ApiClient
  readonly contact: ContactSummaryDto
  readonly fields: readonly ContactFieldDto[]
  readonly onSaved: () => void
  readonly onClose: () => void
}) {
  const [name, setName] = useState(contact.name)
  const [company, setCompany] = useState(contact.company ?? '')
  const [status, setStatus] = useState(contact.status)
  const [custom, setCustom] = useState<Readonly<Record<string, string>>>(contact.custom)
  const [problem, setProblem] = useState<string | undefined>(undefined)
  const [saving, setSaving] = useState(false)

  const save = async () => {
    setSaving(true)
    setProblem(undefined)
    try {
      await client.editContact(contact.contactId, { name, company, status, custom })
      onSaved()
      onClose()
    } catch {
      setProblem('We could not save that contact. Nothing was changed — try again.')
      setSaving(false)
    }
  }

  return (
    <Dialog
      description={contact.email}
      footer={
        <>
          <button
            className="rounded-pill bg-brand px-5 py-2.5 font-bold text-[13px] text-brand-contrast disabled:cursor-not-allowed disabled:opacity-60"
            disabled={saving}
            form="edit-contact"
            type="submit"
          >
            {saving ? 'Saving…' : 'Save changes'}
          </button>
          <button
            className="font-medium text-[13px] text-t3 hover:text-t1"
            onClick={onClose}
            type="button"
          >
            Cancel
          </button>
        </>
      }
      heading="Edit contact"
      onClose={onClose}
    >
      {problem === undefined ? null : <Alert>{problem}</Alert>}

      <form
        id="edit-contact"
        onSubmit={(event) => {
          event.preventDefault()
          void save()
        }}
      >
        <ContactFields
          busy={saving}
          company={company}
          custom={custom}
          fields={fields}
          idPrefix="edit-contact"
          name={name}
          onCompany={setCompany}
          onCustom={(key, value) => setCustom((current) => ({ ...current, [key]: value }))}
          onName={setName}
          onStatus={setStatus}
          status={status}
          statuses={STATES}
        />
      </form>

      {/* Said where it is true rather than in a tooltip: the address is the one
          field here that cannot move, and somebody looking for it should find
          the reason beside it. */}
      <p className="mt-5 border-line border-t pt-4 text-[12px] text-t3">
        The email address cannot be changed. Unsubscribes and the consent record are kept against
        the address itself, so changing it would separate this person from their own history — add a
        new contact instead.
      </p>
    </Dialog>
  )
}
